import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Product, ProductsComponent } from '../../components/products/products.component';
import { SubtitlesComponent } from '../../components/subtitles/subtitles.component';
import { CommonModule } from '@angular/common';
import { SpeechComponent } from '../../components/speech/speech.component';
import { RecommendationsFlowService, CurrentRecommendation } from '../services/recommendations-flow.service';
import { SelectedProductsService } from '../services/selected-products.service';
import { ApiService } from '../../service/api.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-step4-xsell',
  standalone: true,
  imports: [CommonModule, SpeechComponent, SubtitlesComponent, ProductsComponent],
  templateUrl: './step4-xsell.component.html',
  styleUrl: './step4-xsell.component.scss'
})
export class Step4XsellComponent implements OnInit, OnDestroy {

  products: Product[] = [];
  currentRecommendation: CurrentRecommendation | null = null;
  private subscriptions = new Subscription();
  actionButtonState: 'waiting' | 'listening' | 'loading' = 'loading';
  selectedProductIds: Set<string> = new Set();

  @ViewChild('speechComponent') speechComponent!: SpeechComponent;
  @ViewChild('subtitles') SubtitlesComponent!: SubtitlesComponent;
  @ViewChild('productComponent', { static: false }) productComponent!: ProductsComponent;
  
  @Output() productSelected = new EventEmitter<Product | null>();
  @Output() stageCompleted = new EventEmitter<void>();

  private stageCompletedEmitted = false;

  constructor(
    private recoFlow: RecommendationsFlowService,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private selectionService: SelectedProductsService
  ) {}

  ngOnInit() {
    // En el flujo integrado, verificar si hay productos del batch de cross-sell
    console.log('xsell ngOnInit');
    console.log('apiService.integratedRecommendationsFlow', this.apiService.integratedRecommendationsFlow);
    if (this.apiService.integratedRecommendationsFlow) {
      // Esperar a que el batch de cross-sell esté cargado
      // No hay timeout corto porque queremos mostrar el cross-sell aunque tarde
      const checkInterval = setInterval(() => {
        if (this.recoFlow.isCrossSellingBatchLoaded()) {
          clearInterval(checkInterval);
          this.loadCrossSellingProducts();
        }
      }, 100);

      // Timeout de seguridad más largo (15 segundos) para evitar esperar indefinidamente
      setTimeout(() => {
        clearInterval(checkInterval);
        if (this.recoFlow.isCrossSellingBatchLoaded()) {
          this.loadCrossSellingProducts();
        } else {
          // Si después de 15 segundos aún no está cargado, verificar si hay productos
          const batchProducts = this.recoFlow.getCrossSellingBatchProducts();
          if (batchProducts.length > 0) {
            // Hay productos aunque no esté marcado como "loaded", mostrarlos de todas formas
            this.loadCrossSellingProducts();
          } else {
            // Si no hay productos, completar la etapa
            console.warn('Cross-sell no respondió a tiempo, omitiendo...');
            this.handleStageCompletion();
          }
        }
      }, 15000);
    }

    // Suscribirse a las recomendaciones del servicio (solo cross-sell) - para flujo legacy
    this.subscriptions.add(
      this.recoFlow.currentRecommendation$.subscribe(recommendation => {
        if (this.apiService.integratedRecommendationsFlow) {
          // En el flujo integrado, no usamos currentRecommendation$ para cross-sell
          return;
        }
        
        if (recommendation && recommendation.type === 'cross') {
          this.currentRecommendation = recommendation;
          this.products = [recommendation.product];
          this.SubtitlesComponent.render(recommendation.message);
          // Mantener el estado del botón en "loading" mientras se eligen productos
          this.actionButtonState = 'loading';
          this.cdr.detectChanges();
          
          if (this.productComponent) {
            this.productComponent.loadProducts();
            setTimeout(() => {
              if (this.productComponent) {
                this.productComponent.refreshSplide();
              }
            }, 200);
          }
        } else if (!recommendation && !this.apiService.integratedRecommendationsFlow) {
          this.currentRecommendation = null;
          this.products = [];
        }
      })
    );

    this.subscriptions.add(
      this.recoFlow.flowFinished$.subscribe(finished => {
        if (finished && !this.apiService.integratedRecommendationsFlow) {
          this.handleStageCompletion();
        }
      })
    );

    this.subscriptions.add(
      this.recoFlow.errorMessage$.subscribe(message => {
        if (!message) {
          return;
        }

        this.currentRecommendation = null;
        this.products = [];
        this.SubtitlesComponent.render(message);
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onProductSelected(product: Product) {
    
    if (this.apiService.integratedRecommendationsFlow) {
      // En el flujo integrado, toggle la selección del producto
      if (this.selectedProductIds.has(product.productID)) {
        // Deseleccionar
        this.selectedProductIds.delete(product.productID);
      } else {
        // Seleccionar
        this.selectedProductIds.add(product.productID);
      }
      this.cdr.detectChanges();
      return;
    }
    this.recoFlow.acceptCurrent(product);
    this.productSelected.emit(product);
  }

  isProductSelected(productId: string): boolean {
    return this.selectedProductIds.has(productId);
  }

  continueToCart() {
    if (this.selectedProductIds.size === 0) {
      return;
    }

    // Cambiar el estado del botón a "waiting" cuando el usuario termina de seleccionar
    this.actionButtonState = 'waiting';
    
    // Agregar todos los productos seleccionados al servicio de selección
    const baseTodoId = 'cross-sell-batch';
    this.products.forEach(product => {
      if (this.selectedProductIds.has(product.productID)) {
        this.selectionService.addCrossSelection(baseTodoId, product);
      }
    });

    // Completar la etapa para ir al carrito
    this.handleStageCompletion();
  }

  noInterest() {
    // Cambiar el estado del botón a "waiting" cuando el usuario termina de seleccionar
    this.actionButtonState = 'waiting';
    
    if (this.apiService.integratedRecommendationsFlow) {
      // En el flujo integrado, simplemente completar la etapa
      this.handleStageCompletion();
      return;
    }
    this.recoFlow.rejectCurrent();
    this.productSelected.emit(null);
  }

  private loadCrossSellingProducts() {
    const batchProducts = this.recoFlow.getCrossSellingBatchProducts();
    const batchMessage = this.recoFlow.getCrossSellingBatchMessage();

    if (batchProducts.length > 0) {
      this.products = batchProducts;
      const message = this.getCrossSellingMessage(
        batchMessage,
        '¿Te gustaría agregar alguno de estos productos?'
      );
      this.SubtitlesComponent.render(message);
      // Mantener el estado del botón en "loading" mientras se eligen productos
      this.actionButtonState = 'loading';
      this.cdr.detectChanges();
      
      if (this.productComponent) {
        this.productComponent.loadProducts();
        setTimeout(() => {
          if (this.productComponent) {
            this.productComponent.refreshSplide();
          }
        }, 200);
      }
    } else {
      // Si no hay productos, completar la etapa
      this.handleStageCompletion();
    }
  }

  /**
   * Determina el mensaje de cross-selling según cross_selling_text_type
   * Si es "custom", usa cross_selling_text del POS
   * Si es "ai_generated", usa el mensaje del backend
   */
  private getCrossSellingMessage(backendMessage?: string, fallback?: string): string {
    const posDetails = this.apiService.details;
    if (!posDetails) {
      return backendMessage || fallback || '';
    }

    // Si cross_selling_text_type es "custom", usar cross_selling_text
    if (posDetails.cross_selling_text_type === 'custom') {
      return posDetails.cross_selling_text || fallback || '';
    }

    // Si es "ai_generated" o cualquier otro valor, usar el mensaje del backend
    return backendMessage || fallback || '';
  }

  private handleStageCompletion() {
    if (!this.apiService.integratedRecommendationsFlow || this.stageCompletedEmitted) {
      return;
    }
    this.stageCompletedEmitted = true;
    this.currentRecommendation = null;
    this.products = [];
    this.stageCompleted.emit();
    this.cdr.detectChanges();
  }

  // Método para compatibilidad con AppComponent (flujo legacy)
  setProducts(recommendations: any) {
    // Ahora puede recibir una lista de productos o un solo producto
    if (recommendations.crosssellingProducts?.productos && Array.isArray(recommendations.crosssellingProducts.productos)) {
      // Lista de productos (nuevo formato)
      this.products = recommendations.crosssellingProducts.productos.map((product: any) => ({
        load: false,
        productID: product.id,
        name: product.name,
        price: product.price,
        image: product.photourl,
        description: product.search_aggregate
      }));
    } else if (recommendations.crosssellingProducts?.producto) {
      // Un solo producto (formato legacy)
      const product = recommendations.crosssellingProducts.producto;
      this.products = [{
        load: false,
        productID: product.id,
        name: product.name,
        price: product.price,
        image: product.photourl,
        description: product.search_aggregate
      }];
    }

    if (this.products.length > 0) {
      const message = this.getCrossSellingMessage(
        recommendations.crosssellingProducts?.explicacionDeRecomendacion,
        this.products.length > 1 ? '¿Te gustaría agregar alguno de estos productos?' : '¿Te gustaría agregar este producto?'
      );
      this.SubtitlesComponent.render(message);
      // Mantener el estado del botón en "loading" mientras se eligen productos
      this.actionButtonState = 'loading';
      this.cdr.detectChanges();
      
      if (this.productComponent) {
        this.productComponent.loadProducts();
        setTimeout(() => {
          if (this.productComponent) {
            this.productComponent.refreshSplide();
          }
        }, 200);
      }
    }
  }
}
